import React, { useEffect, useState } from 'react';
import { Button, DatePicker, Drawer, Input, Space, Tooltip, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { FctEstado, FctIcono, fmtFecha } from '../../fct/react';
import { fct } from '../../fct/tokens/tokens';
import { usePermissions } from '../../contexts/PermissionContext';
import { EncuestaF4, FichaF4, guardarFichaF4 } from '../../api/cxPanelApi';
import VisibilidadF4Switch, { RECURSO_VISIBILIDAD_F4 } from './VisibilidadF4Switch';
import LlamadasMystery from './LlamadasMystery';
import {
  MAX_NOTAS_FCT, datosFicha, diaMadrid, errorPlazo, estadoMystery, mensajeErrorFicha, resumenNotas, tieneNotas,
} from './panelCx';

const { Text } = Typography;

/**
 * Botón de icono «Notas y plazo» de cada F4 (al lado del interruptor de visibilidad). Si la F4 tiene notas
 * (de FCT o de Mystery), el icono va en verde oscuro con un punto y el tooltip enseña su principio.
 */
export const BotonFichaF4: React.FC<{ encuesta: EncuestaF4; onAbrir: () => void }> = ({ encuesta, onAbrir }) => {
  const conNotas = tieneNotas(encuesta);
  const resumen = resumenNotas(encuesta);
  return (
    <Tooltip
      title={(
        <div>
          <div style={{ fontWeight: 600 }}>Notas y plazo</div>
          {resumen?.map((l) => <div key={l}>{l}</div>)}
        </div>
      )}
    >
      <Button
        size="small"
        type="text"
        onClick={onAbrir}
        aria-label={conNotas ? 'Notas y plazo (tiene notas)' : 'Notas y plazo'}
        icon={(
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <FctIcono
              nombre="mensaje"
              style={{ width: 16, height: 16, color: conNotas ? fct.marca['verde-oscuro'] : fct.tinta['500'] }}
            />
            {conNotas && (
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute', top: -2, right: -3, width: 6, height: 6, borderRadius: '50%',
                  background: fct.marca['verde-oscuro'],
                }}
              />
            )}
          </span>
        )}
      />
    </Tooltip>
  );
};

/** Un dato de la ficha: etiqueta pequeña encima y el valor (o el control) debajo. */
const Campo: React.FC<{ etiqueta: React.ReactNode; children: React.ReactNode; ancho?: boolean }> = (
  { etiqueta, children, ancho },
) => (
  <div style={{ minWidth: 0, gridColumn: ancho ? '1 / -1' : undefined }}>
    <div style={{ fontSize: 12, color: fct.tinta['500'], marginBottom: 4 }}>{etiqueta}</div>
    <div style={{ color: fct.tinta['900'] }}>{children}</div>
  </div>
);

/** Texto largo de solo lectura (notas), respetando los saltos de línea. */
const TextoNotas: React.FC<{ texto?: string | null; vacio: string }> = ({ texto, vacio }) => (texto?.trim()
  ? (
    <div style={{
      whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: 260, overflowY: 'auto', padding: '8px 12px',
      background: fct.superficie.hundida, border: `1px solid ${fct.linea.tarjeta}`, borderRadius: 6,
    }}>
      {texto}
    </div>
  )
  : <Text type="secondary">{vacio}</Text>);

const diaLegible = (dia: string | null): string => (dia ? fmtFecha(`${dia}T12:00:00`) : '—');

interface Props {
  /** La F4 abierta (null = cerrada). Se lee de la lista, así el interruptor de aquí y el de la tabla van a la par. */
  encuesta: EncuestaF4 | null;
  onCerrar: () => void;
  /** Tras guardar: la ficha tal como la devuelve el backend, para actualizar la fila. */
  onGuardada: (ficha: FichaF4) => void;
  /** Tras cambiar la visibilidad desde la ficha. */
  onVisibilidad: (id: number, visible: boolean) => void;
}

/**
 * «Ficha F4»: la ficha que CxCp usa hoy en AuraQuantic para cada encuesta F4 (contrato §9). Estado y
 * llamadas sin contacto con su fecha y su nota (solo lectura, D-CX-79), visibilidad para Mystery, plazo, «Notas FCT» (las escribe CxCp y Mystery las lee
 * en su portal) y «Notas de Mystery» (las escribe Mystery; aquí solo se leen). Sin el permiso de la
 * visibilidad F4, todo es de solo lectura.
 */
const FichaF4Drawer: React.FC<Props> = ({ encuesta, onCerrar, onGuardada, onVisibilidad }) => {
  const { hasPermission } = usePermissions();
  const puede = hasPermission(RECURSO_VISIBILIDAD_F4);

  // La última abierta se sigue enseñando mientras el panel se cierra (si no, se vería vacío al salir).
  const [ultima, setUltima] = useState<EncuestaF4 | null>(encuesta);
  if (encuesta && encuesta !== ultima) setUltima(encuesta);
  const e = encuesta ?? ultima;

  const abierta = !!encuesta;
  const id = e?.id;
  const notasOriginales = e?.notasFct ?? '';
  const inicioOriginal = diaMadrid(e?.plazoInicio);
  const finOriginal = diaMadrid(e?.plazoFin);

  const [notas, setNotas] = useState('');
  const [inicio, setInicio] = useState<string | null>(null);
  const [fin, setFin] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  // Al abrir o cerrar una F4 (o si sus datos cambian por debajo), el formulario vuelve a lo guardado: lo que no
  // se guardó no reaparece al volver a abrirla.
  useEffect(() => {
    setNotas(notasOriginales);
    setInicio(inicioOriginal);
    setFin(finOriginal);
  }, [abierta, id, notasOriginales, inicioOriginal, finOriginal]);

  // El plazo solo se valida si se ha tocado: hay F4 antiguas con el fin antes de la creación.
  const plazoTocado = inicio !== inicioOriginal || fin !== finOriginal;
  const sucio = plazoTocado || notas !== notasOriginales;
  const fallo = errorPlazo(inicio, fin, plazoTocado);

  const guardar = async () => {
    if (!encuesta || fallo) return;
    setGuardando(true);
    try {
      const ficha = await guardarFichaF4(encuesta.id, datosFicha(notas, inicio, fin));
      onGuardada({ ...ficha, id: encuesta.id });
      message.success('Ficha guardada');
      onCerrar();
    } catch (err) {
      message.error(mensajeErrorFicha(err), 8);
    } finally {
      setGuardando(false);
    }
  };

  const estado = e ? estadoMystery(e.estadoCod) : null;

  return (
    <Drawer
      open={!!encuesta}
      onClose={onCerrar}
      width={560}
      // Con cambios sin guardar no se cierra por un clic fuera ni con Esc: solo con Cancelar o la X.
      maskClosable={!sucio}
      keyboard={!sucio}
      title={e ? (
        <span>
          Ficha F4 · <span className="fct-mono">{e.codigoCliente || `#${e.id}`}</span> {e.nombreCliente || 'Sin nombre'}
        </span>
      ) : ''}
      footer={puede ? (
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onCerrar} disabled={guardando}>Cancelar</Button>
          <Button type="primary" onClick={guardar} loading={guardando} disabled={!sucio || !!fallo}>Guardar</Button>
        </Space>
      ) : (
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onCerrar}>Cerrar</Button>
        </Space>
      )}
    >
      {e && estado && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 }}>
            <Campo etiqueta="Operación" ancho>
              {e.ccl && <span className="fct-mono">{e.ccl}</span>}
              {e.ccl && e.nombreOperacion ? ' · ' : ''}
              {e.nombreOperacion || (!e.ccl && <Text type="secondary">Sin operación</Text>)}
              {e.rol && (
                <Text type="secondary"> ({e.rol === 'Vendedor' ? 'parte vendedora' : 'parte compradora'})</Text>
              )}
            </Campo>
            <Campo etiqueta="Estado">
              <FctEstado personalizado={{ etiqueta: estado.texto, familia: estado.familia }} />
            </Campo>
            <Campo etiqueta="Visible para Mystery">
              <VisibilidadF4Switch
                id={e.id}
                visible={e.visibleMystery}
                quitadaManual={e.quitadaManual}
                onCambiado={(v) => onVisibilidad(e.id, v)}
              />
            </Campo>
          </div>

          <Campo etiqueta="Llamadas sin contacto (las registra Mystery en su portal; aquí solo se leen)">
            <LlamadasMystery llamadasSinExito={e.llamadasSinExito} notasMystery={e.notasMystery} />
          </Campo>

          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 }}>
              <Campo etiqueta="Plazo de la encuesta · Inicio">
                {puede ? (
                  <DatePicker
                    format="DD/MM/YYYY"
                    allowClear={false}
                    value={inicio ? dayjs(inicio) : null}
                    onChange={(d) => d && setInicio(d.format('YYYY-MM-DD'))}
                    style={{ width: '100%' }}
                    aria-label="Inicio del plazo"
                  />
                ) : <span className="fct-mono">{diaLegible(inicioOriginal)}</span>}
              </Campo>
              <Campo etiqueta="Fin">
                {puede ? (
                  <DatePicker
                    format="DD/MM/YYYY"
                    allowClear={false}
                    value={fin ? dayjs(fin) : null}
                    onChange={(d) => d && setFin(d.format('YYYY-MM-DD'))}
                    status={fallo ? 'error' : undefined}
                    style={{ width: '100%' }}
                    aria-label="Fin del plazo"
                  />
                ) : <span className="fct-mono">{diaLegible(finOriginal)}</span>}
              </Campo>
            </div>
            {fallo && (
              <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, color: fct.estado.riesgo.texto }}>
                <FctIcono nombre="alerta" style={{ width: 14, height: 14 }} />
                {fallo}
              </div>
            )}
          </div>

          <Campo etiqueta="Notas FCT (las lee Mystery en su portal)">
            {puede ? (
              <Input.TextArea
                value={notas}
                onChange={(ev) => setNotas(ev.target.value)}
                maxLength={MAX_NOTAS_FCT}
                showCount
                autoSize={{ minRows: 4, maxRows: 10 }}
                placeholder="Lo que Mystery debe saber antes de llamar"
                aria-label="Notas FCT"
              />
            ) : <TextoNotas texto={e.notasFct} vacio="Sin notas FCT" />}
          </Campo>

          <Campo etiqueta="Notas de Mystery (las escribe Mystery en su portal; aquí solo se leen)">
            <TextoNotas texto={e.notasMystery} vacio="Sin notas de Mystery" />
          </Campo>
        </div>
      )}
    </Drawer>
  );
};

export default FichaF4Drawer;
